<template>
  <div class="evaluation-overview">
    <div
      class="evaluation-overview__grid"
      :style="{
        'grid-template-columns': gridLayout.templateColumns,
        'grid-template-rows': gridLayout.templateRows,
        'grid-template-areas': gridLayout.templateAreas
      }"
    >
      <div
        v-for="tile of tiles"
        :key="`tile-${tile.index}-${tile.evaluationId}`"
        class="evaluation-overview__tile"
        :style="`grid-area: a${tile.index + 1}`"
        @drop.prevent="onDrop($event, tile)"
        @dragover="onDragOver($event, tile)"
        @dragenter="onDragEnter($event, tile)"
        @contextmenu.prevent="onContextMenu($event, tile)"
      >
        <evaluation-chart
          v-if="tile.evaluationId != null && tile.evaluationId in evaluations"
          :evaluation-id="tile.evaluationId"
          :override-settings="tile.overrideSettings"
          :title-clickable="true"
          class="evaluation-overview__chart"
          draggable="true"
          @dragstart="onDragStart($event, tile)"
          @click:title="$emit('open-evaluation', tile.evaluationId)"
        >
          <template #settings="{ settings, bodyWidth, bodyHeight }">
            <chart-settings-list
              :chart="evaluations[tile.evaluationId]"
              :settings="settings"
              :body-width="bodyWidth"
              :body-height="bodyHeight"
              @update="updateTileSettings(tile.index, $event)"
            >
              <template #after>
                <hr />
                <button
                  type="button"
                  class="button button_secondary"
                  @click="removeTile(tile.index)"
                >
                  {{ $t("actions.removeChart") }}
                </button>
              </template>
            </chart-settings-list>
          </template>
        </evaluation-chart>
        <div v-else class="evaluation-overview__empty">
          <button
            type="button"
            class="evaluation-overview__loadbutton"
            @click="openEvaluationPicker(tile.index, $event)"
          >
            {{ $t("actions.loadChart") }}
          </button>
        </div>
      </div>
    </div>

    <context-menu
      v-if="contextMenuData.visible"
      :x="contextMenuData.x"
      :y="contextMenuData.y"
      @close="contextMenuData.visible = false"
    >
      <button
        type="button"
        class="context-menu__entry"
        @click="removeTile(contextMenuData.tileIndex)"
      >
        {{ $t("actions.removeChart") }}
      </button>
    </context-menu>

    <evaluation-loading-menu
      :visible="loadingMenu.tileIndex != null"
      :x="loadingMenu.x"
      :y="loadingMenu.y"
      :open-evaluation-ids="openEvaluationIds"
      @close="loadingMenu.tileIndex = null"
      @open-evaluation="openEvaluation(loadingMenu.tileIndex, $event)"
    />

    <quick-info-popup
      v-if="layoutsMenu.visible"
      :position="{ x: layoutsMenu.x, y: layoutsMenu.y }"
      @close="layoutsMenu.visible = false"
      @pointerdown:outside="layoutsMenu.visible = false"
    >
      <template #title>
        {{ $t("labels.layout") }}
      </template>
      <template #default>
        <evaluation-overview-layouts :overview-id="evaluationOverviewId" />
      </template>
    </quick-info-popup>
  </div>
</template>

<script lang="ts">
import { notNull } from "@/helpers/utility/Type";

import { charts } from "@editor/items/charts/charts";
import { useEvaluationStore } from "./evaluations.store";
import { useEvaluationOverviewStore } from "./evaluationOverviews.store";
import { overviewLayouts } from "./evaluationOverviews.store";

import QuickInfoPopup from "@core/ui/QuickInfoPopup.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import EvaluationChart from "@editor/items/evaluation/EvaluationChart.vue";
import ChartSettingsList from "@editor/items/charts/ChartSettingsList.vue";
import EvaluationOverviewLayouts from "./EvaluationOverviewLayouts.vue";
import EvaluationLoadingMenu from "./EvaluationLoadingMenu.vue";

import type { ChartSettings } from "@editor/items/charts/ChartItem";

export default {
  components: {
    ContextMenu,
    QuickInfoPopup,
    EvaluationChart,
    ChartSettingsList,
    EvaluationOverviewLayouts,
    EvaluationLoadingMenu
  },
  props: {
    evaluationOverviewId: {
      type: Number,
      required: true
    }
  },
  emits: ["open-evaluation"],
  setup() {
    return { overviewLayouts, charts };
  },
  data() {
    return {
      layoutsMenu: {
        visible: false,
        x: 0,
        xOffset: 0,
        y: 0
      },
      loadingMenu: {
        tileIndex: null as null | number,
        x: 0,
        y: 0
      },
      contextMenuData: {
        visible: false,
        tileIndex: 0,
        x: 0,
        y: 0
      }
    };
  },
  computed: {
    evaluations() {
      return useEvaluationStore().items;
    },
    evaluationOverview() {
      return useEvaluationOverviewStore().items[this.evaluationOverviewId];
    },
    tiles() {
      return new Array(this.gridLayout.tiles).fill(null).map((_, index) => {
        const tile = this.evaluationOverview.tiles[index];
        if (notNull(tile)) {
          return tile;
        }
        return { index, evaluationId: null };
      });
    },
    openEvaluationIds() {
      return new Set(
        this.tiles.map((tile) => tile.evaluationId).filter(notNull)
      );
    },
    unopenedEvaluations() {
      return useEvaluationStore().orderedItems.filter(
        (item) => !this.tiles.find((tile) => tile.evaluationId == item.id)
      );
    },
    gridLayout() {
      return this.overviewLayouts[this.evaluationOverview.layout];
    }
  },
  methods: {
    openEvaluationPicker(tileIndex: number, mouseEvent: MouseEvent) {
      this.loadingMenu.tileIndex = tileIndex;
      this.loadingMenu.x = mouseEvent.clientX;
      this.loadingMenu.y = mouseEvent.clientY;
    },
    openEvaluation(tileIndex: number | null, evaluationId: number) {
      if (tileIndex == null) return;
      this.loadingMenu.tileIndex = null;

      useEvaluationOverviewStore().openEvaluation(
        this.evaluationOverviewId,
        tileIndex,
        evaluationId
      );
    },
    updateTileSettings(tileIndex: number, settings: Partial<ChartSettings>) {
      useEvaluationOverviewStore().updateTileSettings(
        this.evaluationOverviewId,
        tileIndex,
        settings
      );
    },
    removeTile(tileIndex: number) {
      useEvaluationOverviewStore().removeTile(
        this.evaluationOverviewId,
        tileIndex
      );
    },

    openLayouts(event?: MouseEvent) {
      this.layoutsMenu.visible = true;
      if (event != null) {
        this.layoutsMenu.x = event.clientX - 50;
        this.layoutsMenu.y = event.clientY - 20;
      }
    },

    onDragStart(event: DragEvent, tile: { index: number }) {
      event.dataTransfer?.setData(
        "text/evaluationoverviewtileindex",
        tile.index.toString()
      );
    },
    onDrop(event: DragEvent, tile: { index: number }) {
      if (event.dataTransfer == null) return;

      const sourceTileIndex = event.dataTransfer.getData(
        "text/evaluationoverviewtileindex"
      );
      if (sourceTileIndex == null) return;

      useEvaluationOverviewStore().swapTiles(
        this.evaluationOverviewId,
        Number(sourceTileIndex),
        tile.index
      );
    },
    onDragOver(event: DragEvent, _tile: { index: number }) {
      if (event.dataTransfer == null) return;
      if (
        !event.dataTransfer.types.includes("text/evaluationoverviewtileindex")
      ) {
        return;
      }
      // accept the transfer by canceling the event
      event.preventDefault();

      event.dataTransfer.dropEffect = "move";
    },
    onDragEnter(event: DragEvent, _tile: { index: number }) {
      if (event.dataTransfer == null) return;
      if (
        !event.dataTransfer.types.includes("text/evaluationoverviewtileindex")
      ) {
        return;
      }
      // accept the transfer by canceling the event
      event.preventDefault();
    },

    onContextMenu(
      event: MouseEvent,
      tile: { index: number; evaluationId: number | null }
    ) {
      if (tile.evaluationId == null) return;
      this.contextMenuData.visible = true;
      this.contextMenuData.tileIndex = tile.index;
      this.contextMenuData.x = event.clientX;
      this.contextMenuData.y = event.clientY;
    }
  }
};
</script>

<style lang="scss">
.evaluation-overview {
  background-color: var(--color-background-deep);

  &__grid {
    height: 100%;
    display: grid;
    gap: 1px;
    min-height: 0;
    overflow: hidden;
  }

  &__tile {
    background-color: var(--color-background);
    display: grid;
    justify-content: stretch;
    min-height: 0;
  }
  &__chart {
    width: 100%;
    outline: 1px solid var(--color-grey-light);
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__loadbutton {
    align-self: center;
    justify-self: center;
    border: 1px solid var(--color-border);
    background-color: var(--color-background-close);
    padding: 0.5rem 1rem;
    box-shadow: 0 2px 5px var(--color-shadow-1);
    outline: 1px solid transparent;
    transition: outline-color 250ms, color 250ms;

    &:hover {
      outline: 1px solid var(--color-action);
      color: var(--color-action);
    }
  }
}
</style>
